(() => {
  'use strict';

  const elementName = 'kiekkokingi-restock-countdown';
  if (customElements.get(elementName)) return;

  class KiekkoKingiRestockCountdown extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToTimer) return;
      this.isConnectedToTimer = true;
      this.automatic = this.dataset.automaticLinks === 'true';
      this.timeZone = this.automatic ? 'Europe/Helsinki' : this.dataset.timeZone || 'Europe/Helsinki';
      this.weekday = this.automatic ? 0 : this.numberFromData('weekday', 0, 0, 6);
      this.hour = this.numberFromData(this.automatic ? 'releaseHour' : 'hour', 11, 0, 23);
      this.minute = this.numberFromData(this.automatic ? 'releaseMinute' : 'minute', 0, 0, 59);
      this.linkDaysBefore = this.numberFromData('linkDaysBefore', 1, 0, 6);
      this.linkHour = this.numberFromData('linkHour', 16, 0, 23);
      this.linkMinute = this.numberFromData('linkMinute', 0, 0, 59);
      this.showDays = this.booleanFromData('showDays', true);
      this.showHours = this.booleanFromData('showHours', true);
      this.showMinutes = this.booleanFromData('showMinutes', true);
      this.showSeconds = this.booleanFromData('showSeconds', true);
      this.publishedUntilEnd = this.automatic || this.booleanFromData('publishedUntilEnd', true);
      this.countdown = this.querySelector('.kk-home-used-discs__restock-countdown');
      this.fallback = this.querySelector('.kk-home-used-discs__restock-fallback');
      this.published = this.querySelector('.kk-home-used-discs__restock-published');
      this.screenReader = this.querySelector('[data-screen-reader]');
      this.values = new Map(
        Array.from(this.querySelectorAll('[data-unit]')).map((unit) => [
          unit.dataset.unit,
          unit.querySelector('[data-value]'),
        ]),
      );
      this.hasCountdown = !!this.countdown && this.values.size > 0;
      if (!this.hasCountdown && !this.automatic) return;
      this.entries = new Map();
      this.releaseTimes = new Map();
      this.linkTimes = new Map();
      this.refreshAttempts = 0;
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      try {
        this.partsFormatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        });
        this.accessibleFormatter = new Intl.DateTimeFormat(this.dataset.locale || document.documentElement.lang || 'fi-FI', {
          timeZone: this.timeZone,
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        });
        if (this.automatic) this.readSchedule(this);
        this.update();
        this.start();
      } catch {
        this.stop();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.onVisibilityChange = undefined;
      }
    }

    disconnectedCallback() {
      this.isConnectedToTimer = false;
      this.stop();
      this.cancelRefresh();
      this.calendarDay = undefined;
      this.refreshKey = undefined;
      this.successfulRefreshKey = undefined;
      if (this.onVisibilityChange) {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }
    }

    booleanFromData(key, fallback) {
      const value = this.dataset[key];
      if (value === undefined) return fallback;
      return value === 'true';
    }

    numberFromData(key, fallback, minimum, maximum) {
      const parsed = Number.parseInt(this.dataset[key], 10);
      if (!Number.isFinite(parsed)) return fallback;
      return Math.min(maximum, Math.max(minimum, parsed));
    }

    parseDate(value) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
      const [year, month, day] = value.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) return null;
      return { year, month, day };
    }

    dateKey({ year, month, day }) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    releaseAt(key) {
      if (!this.releaseTimes.has(key)) {
        const date = this.parseDate(key);
        this.releaseTimes.set(key, date ? this.wallTimeToDate(date.year, date.month, date.day, this.hour, this.minute).getTime() : Infinity);
      }
      return this.releaseTimes.get(key);
    }

    linkAt(key) {
      if (!this.linkTimes.has(key)) {
        const date = this.parseDate(key);
        // Move calendar dates before resolving Helsinki time so DST changes cannot shift the opening hour.
        const linkDate = date && this.calendarDate(date.year, date.month, date.day, -this.linkDaysBefore);
        this.linkTimes.set(key, linkDate ? Math.min(this.releaseAt(key), this.wallTimeToDate(
          linkDate.year, linkDate.month, linkDate.day, this.linkHour, this.linkMinute,
        ).getTime()) : Infinity);
      }
      return this.linkTimes.get(key);
    }

    readSchedule(root) {
      const node = root.querySelector('[data-restock-schedule]');
      const entries = JSON.parse(node?.textContent || '[]');
      if (!Array.isArray(entries)) throw new Error('Invalid restock schedule');
      const nextEntries = new Map();
      entries.forEach((entry) => {
        if (entry && this.parseDate(entry.date) && !nextEntries.has(entry.date)) {
          nextEntries.set(entry.date, { ...entry, available: entry.available === true });
        }
      });
      this.entries = nextEntries;
      const renderedAt = Number(root.dataset.renderedAt) * 1000;
      this.renderedAt = Number.isFinite(renderedAt) && renderedAt > 0 ? renderedAt : 0;
    }

    verifiedUrl(entry) {
      if (!entry?.available || typeof entry.url !== 'string' || !entry.url) return null;
      try {
        const url = new URL(entry.url, location.origin);
        const prefix = new URL(this.dataset.collectionsUrl || '/collections', location.origin).pathname.replace(/\/$/, '') + '/';
        return url.origin === location.origin && url.pathname.startsWith(prefix) ? url.pathname + url.search : null;
      } catch {
        return null;
      }
    }

    updateLinks(now) {
      let latestDate = '';
      let latestUrl = null;
      this.entries.forEach((entry, date) => {
        const url = this.verifiedUrl(entry);
        if (url && date > latestDate && this.linkAt(date) <= now) {
          latestDate = date;
          latestUrl = url;
        }
      });
      const owner = document.getElementById(this.dataset.ownerId);
      if (!owner || !owner.contains(this)) return;
      owner.querySelectorAll('[data-restock-auto-link]').forEach((link) => {
        if (link.hidden !== !latestUrl) link.hidden = !latestUrl;
        if (latestUrl && link.getAttribute('href') !== latestUrl) link.setAttribute('href', latestUrl);
        else if (!latestUrl && link.hasAttribute('href')) link.removeAttribute('href');
      });
    }

    partsAt(date) {
      const values = {};
      this.partsFormatter.formatToParts(date).forEach((part) => {
        if (part.type !== 'literal') values[part.type] = Number.parseInt(part.value, 10);
      });
      return {
        year: values.year,
        month: values.month,
        day: values.day,
        hour: values.hour,
        minute: values.minute,
        second: values.second,
      };
    }

    wallTimeToDate(year, month, day, hour, minute) {
      const desiredWallTime = Date.UTC(year, month - 1, day, hour, minute, 0);
      let instant = desiredWallTime;

      for (let attempt = 0; attempt < 5; attempt += 1) {
        const actual = this.partsAt(new Date(instant));
        const actualWallTime = Date.UTC(
          actual.year,
          actual.month - 1,
          actual.day,
          actual.hour,
          actual.minute,
          actual.second,
        );
        const correction = desiredWallTime - actualWallTime;
        instant += correction;
        if (correction === 0) break;
      }

      return new Date(instant);
    }

    calendarDate(year, month, day, offsetDays) {
      const date = new Date(Date.UTC(year, month - 1, day + offsetDays));
      return {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth() + 1,
        day: date.getUTCDate(),
      };
    }

    scheduleAt(now) {
      const localNow = this.partsAt(now);
      const calendarToday = Date.UTC(localNow.year, localNow.month - 1, localNow.day);
      const todayWeekday = new Date(calendarToday).getUTCDay();
      const daysUntil = (this.weekday - todayWeekday + 7) % 7;
      let targetCalendar = this.calendarDate(localNow.year, localNow.month, localNow.day, daysUntil);
      let target = this.wallTimeToDate(
        targetCalendar.year,
        targetCalendar.month,
        targetCalendar.day,
        this.hour,
        this.minute,
      );

      if (todayWeekday === this.weekday && now.getTime() >= target.getTime()) {
        const tomorrow = this.calendarDate(localNow.year, localNow.month, localNow.day, 1);
        const endOfReleaseDay = this.wallTimeToDate(
          tomorrow.year,
          tomorrow.month,
          tomorrow.day,
          0,
          0,
        );

        if (this.publishedUntilEnd && now.getTime() < endOfReleaseDay.getTime()) {
          return { state: 'published', target, end: endOfReleaseDay };
        }

        targetCalendar = this.calendarDate(localNow.year, localNow.month, localNow.day, 7);
        target = this.wallTimeToDate(
          targetCalendar.year,
          targetCalendar.month,
          targetCalendar.day,
          this.hour,
          this.minute,
        );
      }

      return { state: 'countdown', target };
    }

    update() {
      if (!this.isConnectedToTimer || !this.isConnected || !this.partsFormatter) return;
      const now = new Date();
      const schedule = this.scheduleAt(now);
      const targetKey = this.dateKey(this.partsAt(schedule.target));
      const published = schedule.state === 'published';
      this.targetKey = targetKey;
      this.updateInterval = this.automatic || (!published && this.showSeconds) ? 1000 : 60000;
      if (this.automatic) this.updateLinks(now.getTime());

      if (this.hasCountdown) {
        if (published && (!this.automatic || this.verifiedUrl(this.entries.get(targetKey)))) {
          this.showPublished(schedule);
        } else if (published) {
          this.showWaiting();
        } else {
          this.showCountdown(schedule, now);
        }
      }

      if (this.automatic) {
        const today = this.dateKey(this.partsAt(now));
        const changedDay = this.calendarDay !== undefined && this.calendarDay !== today;
        this.calendarDay = today;
        const linkOpen = now.getTime() >= this.linkAt(targetKey);
        const phase = published ? 'published' : linkOpen ? 'link-open' : 'before-link';
        const key = `${today}|${targetKey}|${phase}`;
        const staleDay = this.renderedAt && this.dateKey(this.partsAt(new Date(this.renderedAt))) !== today;
        const staleLink = linkOpen && this.renderedAt < this.linkAt(targetKey);
        const staleRelease = published && this.renderedAt < this.releaseAt(targetKey);
        const alreadyRefreshed = this.successfulRefreshKey === key;
        if (changedDay || !this.entries.has(targetKey) ||
          ((linkOpen || published) && !this.verifiedUrl(this.entries.get(targetKey))) ||
          (!alreadyRefreshed && (staleDay || staleLink || staleRelease))) {
          this.requestRefresh(key);
        }
      }

      if (this.intervalId && this.activeInterval !== this.updateInterval) this.start();
    }

    showWaiting() {
      if (this.fallback) this.fallback.hidden = false;
      if (this.countdown) this.countdown.hidden = true;
      if (this.published) {
        this.published.hidden = true;
        this.published.textContent = '';
      }
      if (this.screenReader) this.screenReader.textContent = '';
      this.accessibleStateKey = undefined;
    }

    showPublished(schedule) {
      if (this.fallback) this.fallback.hidden = true;
      this.countdown.hidden = true;
      const stateKey = `published:${schedule.target.getTime()}`;
      if (stateKey !== this.accessibleStateKey) {
        const message = this.dataset.publishedText || '';
        if (this.published) {
          this.published.textContent = message;
          this.published.hidden = !message;
        }
        if (this.screenReader) this.screenReader.textContent = '';
        this.accessibleStateKey = stateKey;
      }
    }

    showCountdown(schedule, now) {
      if (this.fallback) this.fallback.hidden = true;
      if (this.published) {
        this.published.hidden = true;
        this.published.textContent = '';
      }
      this.countdown.hidden = false;

      const remainingSeconds = Math.max(0, Math.floor((schedule.target.getTime() - now.getTime()) / 1000));
      const days = this.showDays ? Math.floor(remainingSeconds / 86400) : 0;
      const afterDays = this.showDays ? remainingSeconds % 86400 : remainingSeconds;
      const hours = this.showHours ? Math.floor(afterDays / 3600) : 0;
      const afterHours = this.showHours ? afterDays % 3600 : afterDays;
      const minutes = this.showMinutes ? Math.floor(afterHours / 60) : 0;
      const seconds = this.showSeconds ? afterHours % 60 : 0;

      this.setValue('days', days, 2);
      this.setValue('hours', hours, 2);
      this.setValue('minutes', minutes, 2);
      this.setValue('seconds', seconds, 2);

      const stateKey = `countdown:${schedule.target.getTime()}`;
      if (stateKey !== this.accessibleStateKey && this.screenReader) {
        const template = this.dataset.accessibleTemplate || '{date}';
        this.screenReader.textContent = template.replace('{date}', this.accessibleFormatter.format(schedule.target));
        this.accessibleStateKey = stateKey;
      }
    }

    setValue(unit, value, minimumLength) {
      const element = this.values.get(unit);
      if (!element) return;
      element.textContent = String(value).padStart(minimumLength, '0');
    }

    requestRefresh(key) {
      if (!this.automatic || !this.isConnectedToTimer || !this.isConnected || document.hidden ||
        window.Shopify?.designMode || !this.dataset.sectionId) return;
      if (this.refreshKey !== key) {
        this.cancelRefresh();
        this.refreshKey = key;
        this.refreshAttempts = 0;
      }
      if (this.refreshPending || this.retryTimer || this.refreshAttempts >= 4) return;
      this.refreshSchedule(key);
    }

    cancelRefresh() {
      this.refreshController?.abort();
      this.refreshController = null;
      window.clearTimeout(this.retryTimer);
      window.clearTimeout(this.requestTimeout);
      this.retryTimer = null;
      this.requestTimeout = null;
      this.refreshPending = false;
    }

    async refreshSchedule(key) {
      this.refreshPending = true;
      this.refreshAttempts += 1;
      const controller = new AbortController();
      this.refreshController = controller;
      this.requestTimeout = window.setTimeout(() => controller.abort(), 8000);
      let succeeded = false;
      try {
        const url = new URL(location.href);
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('_kk_restock_refresh', String(Date.now()));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Restock refresh failed');
        const html = await response.text();
        if (!this.isConnectedToTimer || !this.isConnected || document.hidden || controller.signal.aborted ||
          this.refreshController !== controller || this.refreshKey !== key) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = Array.from(doc.querySelectorAll(elementName)).find((root) => root.id === this.id);
        const incomingSchedule = incoming?.querySelector('[data-restock-schedule]');
        const currentSchedule = this.querySelector('[data-restock-schedule]');
        if (!incoming || !incomingSchedule || !currentSchedule) throw new Error('Invalid restock response');
        this.readSchedule(incoming);
        // Only update collection data: preserve newsletter inputs, selection and listeners.
        currentSchedule.textContent = incomingSchedule.textContent;
        this.dataset.renderedAt = incoming.dataset.renderedAt || '';
        this.successfulRefreshKey = key;
        succeeded = true;
        this.update();
      } catch {
        // Keep the last verified link when collection data cannot be refreshed.
      } finally {
        if (this.refreshController !== controller) return;
        window.clearTimeout(this.requestTimeout);
        this.requestTimeout = null;
        this.refreshController = null;
        this.refreshPending = false;
        const waiting = !this.entries.has(this.targetKey) ||
          (Date.now() >= Math.min(this.linkAt(this.targetKey), this.releaseAt(this.targetKey)) &&
            !this.verifiedUrl(this.entries.get(this.targetKey)));
        if (this.isConnectedToTimer && this.isConnected && !document.hidden && this.refreshKey === key &&
          (!succeeded || waiting) && this.refreshAttempts < 4) {
          const delay = [5000, 30000, 120000][this.refreshAttempts - 1];
          this.retryTimer = window.setTimeout(() => {
            this.retryTimer = null;
            this.requestRefresh(key);
          }, delay);
        }
      }
    }

    start() {
      this.stop();
      if (document.hidden || (!this.hasCountdown && !this.automatic)) return;
      this.activeInterval = this.updateInterval || (this.showSeconds ? 1000 : 60000);
      this.intervalId = window.setInterval(() => this.update(), this.activeInterval);
    }

    stop() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = undefined;
      }
      this.activeInterval = undefined;
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stop();
        this.cancelRefresh();
        return;
      }
      this.update();
      this.start();
    }
  }

  customElements.define(elementName, KiekkoKingiRestockCountdown);
})();
